/**
 * The 3D viewer's own command line ("viewer commands") in the Commands side panel, for
 * backends that have a command language (VisualizerInterface.SupportsNativeCommands()).
 *
 * Everything backend-specific - what is refused, the attribution wording, how commands run -
 * comes from the backend through MainCanvas. Commands only ever run when the user presses
 * Enter (or Run) on text they typed, pasted or recalled from their own history; nothing is
 * executed on load, and typed commands are not saved with BMaps sessions.
 */
import {
    useCallback, useEffect, useLayoutEffect, useRef, useState,
} from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { EventBroker } from 'BMapsSrc/eventbroker';
import {
    supportsNativeCommands, nativeCommandState, runNativeCommand, completeNativeCommand,
} from 'BMapsSrc/MainCanvas';
import { NativeCommandStateEvent } from 'BMapsSrc/VisualizerInterface';
import useEventBrokerSubscription from './common/helpers/useEventBrokerSubscription';

/** The app-side adapter; tests pass their own object with the same five functions. */
export const MainCanvasCommandLine = {
    supported: () => supportsNativeCommands(),
    state: () => nativeCommandState(),
    run: (text) => runNativeCommand(text),
    complete: (text) => completeNativeCommand(text),
    /** "Reapply BMaps display" = the app's own scene redraw, which rebuilds BMaps' molecules. */
    reapply: () => EventBroker.publish('redrawSceneRequest'),
};

export const HistoryStorageKey = 'bmaps.viewerCommandHistory';
const MaxHistory = 200;
const MaxOutputEntries = 2000;
const Monospace = 'ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace';

const KindStyles = {
    echo: { fontWeight: 600, color: '#1f3b57' },
    output: { color: '#222222' },
    error: { color: '#b00020' },
    warning: { color: '#7a5800' },
    refusal: {
        color: '#8a3b00', background: '#fff3e0', borderLeft: '3px solid #e65100', paddingLeft: '4px',
    },
    info: { color: '#555555', fontStyle: 'italic' },
};

/** Per-browser history; storage can be missing or throw (private windows, blocked storage). */
export function loadHistory() {
    try {
        const list = JSON.parse(window.localStorage.getItem(HistoryStorageKey) || '[]');
        return Array.isArray(list) ? list.filter((x) => typeof x === 'string').slice(-MaxHistory) : [];
    } catch (ex) {
        return [];
    }
}

function saveHistory(list) {
    try {
        window.localStorage.setItem(HistoryStorageKey, JSON.stringify(list.slice(-MaxHistory)));
    } catch (ex) {
        // history is a convenience; losing it is fine
    }
}

function safeCall(fn, fallback) {
    try {
        const value = fn();
        return value === undefined ? fallback : value;
    } catch (ex) {
        return fallback;
    }
}

export function commonPrefix(list) {
    if (list.length === 0) return '';
    let prefix = list[0];
    for (const item of list.slice(1)) {
        let i = 0;
        while (i < prefix.length && i < item.length && prefix[i] === item[i]) i++;
        prefix = prefix.slice(0, i);
    }
    return prefix;
}

/** Result lines of a backend: typed lines if it has them, else output / error as plain lines. */
function resultLines(result) {
    if (!result) return [{ kind: 'error', text: 'No result.' }];
    if (Array.isArray(result.lines)) return result.lines;
    const lines = (result.output || []).map((text) => ({ kind: 'output', text: String(text) }));
    if (!result.ok && result.error && !lines.some((l) => l.text === result.error)) {
        lines.push({ kind: 'error', text: String(result.error) });
    }
    return lines;
}

/**
 * @param {{commandLine?: typeof MainCanvasCommandLine, active?: boolean}} props
 *   `active`: the console is the visible tab (focus the input when it becomes visible)
 */
export function NativeCommandConsole({ commandLine=MainCanvasCommandLine, active=true }) {
    const [state, setState] = useState(() => safeCall(() => commandLine.state(), {}));
    const [entries, setEntries] = useState([]);
    const [input, setInput] = useState('');
    const [candidates, setCandidates] = useState([]);
    const [showRestrictions, setShowRestrictions] = useState(false);
    const history = useRef(loadHistory());
    const historyIndex = useRef(-1); // -1: editing a new command
    const draft = useRef('');
    const nextId = useRef(1);
    const outputRef = useRef(null);
    const inputRef = useRef(null);
    const pendingCaret = useRef(null);

    const ready = state && state.status === 'ready';

    const refreshState = useCallback((evtName, published) => {
        setState(published && typeof published === 'object' ? published
            : safeCall(() => commandLine.state(), {}));
    }, [commandLine]);
    useEventBrokerSubscription(NativeCommandStateEvent, refreshState);
    useEffect(() => { refreshState(); }, [refreshState]);

    useEffect(() => {
        const el = outputRef.current;
        if (el) el.scrollTop = el.scrollHeight;
    }, [entries]);

    useLayoutEffect(() => {
        if (pendingCaret.current == null || !inputRef.current) return;
        const caret = pendingCaret.current;
        pendingCaret.current = null;
        inputRef.current.setSelectionRange(caret, caret);
    });

    // Take focus when the person opens the tab, or when it becomes usable while they are on it
    // and nothing else has focus - not because the backend flipped to ready ~10 s after a
    // switch while they were typing somewhere else.
    const wasActive = useRef(false);
    useEffect(() => {
        const justActivated = active && !wasActive.current;
        wasActive.current = active;
        if (!active || !ready || !inputRef.current) return;
        const focused = typeof document !== 'undefined' ? document.activeElement : null;
        const elsewhere = focused && focused !== document.body && focused !== inputRef.current;
        if (justActivated || !elsewhere) inputRef.current.focus();
    }, [active, ready]);

    const append = useCallback((newEntries) => {
        setEntries((prev) => {
            const added = newEntries.map((e) => ({ ...e, id: nextId.current++ }));
            return [...prev, ...added].slice(-MaxOutputEntries);
        });
    }, []);

    const submit = useCallback(() => {
        const text = input;
        if (!text.trim() || !ready) return;
        const list = history.current;
        if (list[list.length - 1] !== text) list.push(text);
        history.current = list.slice(-MaxHistory);
        saveHistory(history.current);
        historyIndex.current = -1;
        draft.current = '';
        const echo = text.replace(/\r\n?/g, '\n').split('\n').map((l) => ({ kind: 'echo', text: l }));
        const result = safeCall(() => commandLine.run(text), null);
        append([...echo, ...resultLines(result)]);
        if (result && typeof result.overlay === 'boolean') {
            setState((prev) => ({ ...prev, overlay: result.overlay }));
        }
        setInput('');
        setCandidates([]);
    }, [input, ready, commandLine, append]);

    const reapply = useCallback(() => {
        safeCall(() => commandLine.reapply(), null);
        setState((prev) => ({ ...prev, overlay: false }));
        append([{ kind: 'info', text: 'Reapplied BMaps display.' }]);
    }, [commandLine, append]);

    const recall = (step) => {
        const list = history.current;
        if (list.length === 0) return false;
        let index = historyIndex.current;
        if (index === -1) {
            if (step > 0) return false;
            draft.current = input;
            index = list.length;
        }
        index += step;
        if (index < 0) return true; // stay on the oldest entry
        if (index >= list.length) {
            historyIndex.current = -1;
            setInput(draft.current);
            pendingCaret.current = draft.current.length;
            return true;
        }
        historyIndex.current = index;
        setInput(list[index]);
        pendingCaret.current = list[index].length;
        return true;
    };

    const complete = (target) => {
        const before = input.slice(0, target.selectionStart);
        const after = input.slice(target.selectionEnd);
        const found = safeCall(() => commandLine.complete(before), []);
        const list = Array.isArray(found) ? found.filter((c) => typeof c === 'string') : [];
        if (list.length === 0) {
            setCandidates([]);
            return;
        }
        const chosen = list.length === 1 ? list[0] : commonPrefix(list);
        if (chosen.length > before.length) {
            setInput(chosen + after);
            pendingCaret.current = chosen.length;
        }
        if (list.length > 1) {
            // show what differs: the word being completed, not the whole line
            const base = before.slice(0, before.length - /[^\s,;()]*$/.exec(before)[0].length);
            setCandidates(list.map((c) => (c.startsWith(base) ? c.slice(base.length) : c).replace(/[\s,]+$/, '')));
        } else {
            setCandidates([]);
        }
    };

    const onKeyDown = (e) => {
        const target = e.target;
        const plain = !e.altKey && !e.ctrlKey && !e.metaKey;
        if (e.key === 'Enter' && !e.shiftKey && plain && !(e.nativeEvent && e.nativeEvent.isComposing)) {
            e.preventDefault();
            submit();
        } else if (e.key === 'Tab' && !e.shiftKey && plain && input.trim() !== '') {
            e.preventDefault(); // with an empty input, Tab moves focus as usual
            complete(target);
        } else if (e.key === 'ArrowUp' && plain && !input.slice(0, target.selectionStart).includes('\n')) {
            if (recall(-1)) e.preventDefault();
        } else if (e.key === 'ArrowDown' && plain && !input.slice(target.selectionEnd).includes('\n')) {
            if (recall(1)) e.preventDefault();
        } else if (e.key === 'Escape') {
            setCandidates([]);
        }
    };

    const attribution = state && state.attribution;
    const trademark = state && state.trademark;
    const restrictions = (state && Array.isArray(state.restrictions)) ? state.restrictions : [];

    return (
        <div
            data-testid="native-command-console"
            style={{
                display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', padding: '0.5rem 1rem 1rem',
            }}
        >
            <Box sx={{
                display: 'flex', alignItems: 'baseline', gap: '0.5em', flexWrap: 'wrap',
            }}
            >
                <Typography variant="subtitle2" component="h3">Viewer command line</Typography>
                {!!attribution && (
                    <Typography variant="caption" data-testid="native-command-attribution">{attribution}</Typography>
                )}
            </Box>
            {!!trademark && (
                <Typography
                    variant="caption"
                    color="textSecondary"
                    data-testid="native-command-trademark"
                    sx={{ fontSize: '0.65rem', display: 'block' }}
                >
                    {trademark}
                </Typography>
            )}
            {!ready && (
                <Typography role="status" data-testid="native-command-status" sx={{ fontStyle: 'italic', fontSize: '0.85rem', margin: '0.25rem 0' }}>
                    {(state && state.message) || 'The 3D viewer is not ready.'}
                </Typography>
            )}
            <div
                ref={outputRef}
                data-testid="native-command-output"
                aria-live="polite"
                style={{
                    flex: '1 1 auto',
                    minHeight: '8rem',
                    overflowY: 'auto',
                    padding: '0.4rem',
                    marginTop: '0.25rem',
                    border: '1px solid lightgray',
                    borderRadius: '4px 4px 0 0',
                    background: 'whitesmoke',
                    fontFamily: Monospace,
                    fontSize: '0.8rem',
                }}
            >
                {entries.length === 0 ? (
                    <Typography sx={{ fontStyle: 'italic', fontSize: '0.85rem' }}>
                        No commands yet. Try: show sticks, organic - or type help.
                    </Typography>
                ) : entries.map((entry) => (
                    <pre
                        key={entry.id}
                        data-kind={entry.kind}
                        style={{
                            margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'inherit', ...(KindStyles[entry.kind] || KindStyles.output),
                        }}
                    >
                        {entry.kind === 'echo' ? `> ${entry.text}` : entry.text}
                    </pre>
                ))}
            </div>
            {!!(state && state.overlay) && (
                <Box
                    data-testid="native-command-overlay"
                    sx={{
                        display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.3em 0.5em', padding: '0.3rem 0.4rem', background: '#fff8e1', border: '1px solid lightgray', borderTop: 0,
                    }}
                >
                    <Typography sx={{ fontSize: '0.8rem', flex: '1 1 12rem' }}>
                        Your commands changed how BMaps&apos; molecules look.
                    </Typography>
                    <Button size="small" variant="outlined" onClick={reapply} sx={{ whiteSpace: 'nowrap' }}>Reapply BMaps display</Button>
                </Box>
            )}
            <div style={{
                padding: '0.4rem', border: '1px solid lightgray', borderTop: 0, borderRadius: '0 0 4px 4px', background: 'white',
            }}
            >
                <TextField
                    fullWidth
                    multiline
                    maxRows={6}
                    size="small"
                    disabled={!ready}
                    value={input}
                    placeholder={ready ? 'Type a command, e.g. color red, organic' : ''}
                    onChange={(e) => {
                        setInput(e.target.value);
                        historyIndex.current = -1;
                        if (candidates.length) setCandidates([]);
                    }}
                    onKeyDown={onKeyDown}
                    inputRef={inputRef}
                    slotProps={{
                        htmlInput: {
                            'aria-label': attribution ? `Viewer command (${attribution})` : 'Viewer command',
                            spellCheck: false,
                            autoCapitalize: 'off',
                            autoCorrect: 'off',
                            'data-testid': 'native-command-input',
                            style: { fontFamily: Monospace, fontSize: '0.85rem' },
                        },
                    }}
                />
                {candidates.length > 0 && (
                    <Typography
                        data-testid="native-command-candidates"
                        sx={{
                            fontFamily: Monospace, fontSize: '0.75rem', color: '#444', marginTop: '0.2rem', wordBreak: 'break-word',
                        }}
                    >
                        {candidates.join('   ')}
                    </Typography>
                )}
                <Box sx={{
                    display: 'flex', alignItems: 'center', gap: '0.5em', marginTop: '0.4rem',
                }}
                >
                    <Button size="small" variant="contained" disabled={!ready || !input.trim()} onClick={submit}>Run</Button>
                    <Button size="small" onClick={() => setEntries([])}>Clear</Button>
                    <Typography variant="caption" sx={{ marginLeft: 'auto' }}>
                        {'Restricted mode · '}
                        <Button
                            size="small"
                            variant="text"
                            aria-expanded={showRestrictions}
                            aria-controls="native-command-restrictions"
                            onClick={() => setShowRestrictions((v) => !v)}
                            sx={{
                                padding: 0, minWidth: 0, fontSize: 'inherit', textTransform: 'none', textDecoration: 'underline', verticalAlign: 'baseline',
                            }}
                        >
                            What is refused?
                        </Button>
                    </Typography>
                </Box>
                <Typography variant="caption" component="div" sx={{ color: '#666', marginTop: '0.2rem' }}>
                    Enter runs, Shift+Enter adds a line, Tab completes, ↑/↓ history.
                </Typography>
                {showRestrictions && (
                    <Box component="ul" id="native-command-restrictions" data-testid="native-command-restrictions" sx={{ margin: '0.3rem 0 0', paddingLeft: '1.2rem', fontSize: '0.75rem' }}>
                        {restrictions.map((text) => <li key={text}>{text}</li>)}
                    </Box>
                )}
            </div>
        </div>
    );
}

/** Is the active backend's command line available? Re-checked when asked (see CommandPanel). */
export function nativeCommandsSupported(commandLine=MainCanvasCommandLine) {
    return !!safeCall(() => commandLine.supported(), false);
}
